import type { Lead } from "@/api_types/school";
import FetchService from "@/services/fetch.service";
import { defineStore } from "pinia";
import type { TrialMembership } from "@/stores/support/trials";

const upsertLead = (leads: TrialMembership[], updated: TrialMembership) => {
  const index = leads.findIndex((l) => l.trial_id === updated.trial_id);
  if (index !== -1) {
    leads.splice(index, 1, updated);
  } else {
    leads.unshift(updated);
  }
};

export const useLeads = defineStore("support/leads", {
  state: () => ({
    leads: [] as TrialMembership[],
    loading: false,
  }),

  actions: {
    async loadLeads() {
      this.loading = true;

      try {
        const response = (await FetchService.fetchData("/trials", "get")) as
          | { data: TrialMembership[] }
          | TrialMembership[];

        let rows: TrialMembership[] = [];
        if (response && "data" in response) {
          rows = response.data || [];
        } else if (Array.isArray(response)) {
          rows = response;
        }

        // Show all prospects — rows stay on Leads even after demo/trial dates are set
        this.leads = rows.sort(
          (a, b) =>
            new Date(b.created_tstamp).getTime() - new Date(a.created_tstamp).getTime()
        );
      } catch (error) {
        console.error("Error loading leads:", error);
        this.leads = [];
      } finally {
        this.loading = false;
      }
    },

    async createLead(lead: Lead): Promise<{ status: boolean; message?: string }> {
      try {
        const payload: Lead & {
          username?: string;
          password?: string;
          timezone?: string;
        } = { ...lead, request_type: "lead" as const };

        // Shared api-dev/prod may still require these fields for leads
        if (!payload.username) {
          payload.username = `lead_${Date.now()}`;
        }
        if (!payload.password) {
          payload.password = Math.random().toString(36).slice(2, 10);
        }
        if (!payload.timezone) {
          payload.timezone = "America/New_York";
        }

        const res = await FetchService.fetchData(`/trials`, "post", payload, true);
        if (res.res.ok) {
          await this.loadLeads();
          return { status: true, message: res.data?.message || "Lead created successfully" };
        }
        return {
          status: false,
          message: res.data?.message || res.data || "Failed to create lead",
        };
      } catch (error) {
        console.error("Error creating lead:", error);
        return { status: false, message: "Failed to create lead" };
      }
    },

    async updateLead(
      trialId: string,
      leadData: Partial<TrialMembership>
    ): Promise<{ status: boolean; message?: string; data?: TrialMembership }> {
      try {
        const res = await FetchService.fetchData(`/trials/${trialId}`, "put", leadData, true);
        if (res.res.ok) {
          const updated = res.data?.data as TrialMembership | undefined;
          if (updated) {
            upsertLead(this.leads, updated);
          } else {
            await this.loadLeads();
          }
          return {
            status: true,
            message: res.data?.message || "Lead updated successfully",
            data: updated,
          };
        }
        return {
          status: false,
          message: res.data?.message || res.data || "Failed to update lead",
        };
      } catch (error) {
        console.error("Error updating lead:", error);
        return { status: false, message: "Failed to update lead" };
      }
    },

    /**
     * Convert a lead into a trial by approving it (status=pending).
     * Stamps trial_date and queues the activation email.
     * Row stays on the Leads list.
     */
    async createTrialFromLead(
      trialId: string
    ): Promise<{ status: boolean; message?: string; data?: TrialMembership }> {
      try {
        const res = await FetchService.fetchData(
          `/trials/${trialId}`,
          "put",
          { status: "pending", request_type: "trial" },
          true
        );
        if (res.res.ok) {
          const updated = res.data?.data as TrialMembership | undefined;
          if (updated) {
            upsertLead(this.leads, updated);
          } else {
            await this.loadLeads();
          }
          return {
            status: true,
            message: res.data?.message || "Trial approved — activation email sent",
            data: updated,
          };
        }
        return {
          status: false,
          message: res.data?.message || res.data || "Failed to create trial from lead",
        };
      } catch (error) {
        console.error("Error creating trial from lead:", error);
        return { status: false, message: "Failed to create trial from lead" };
      }
    },

    async scheduleDemo(
      trialId: string,
      demoDate: string
    ): Promise<{
      status: boolean;
      message?: string;
      data?: TrialMembership;
      zoom_join_url?: string;
      google_event_id?: string;
      warnings?: string[];
    }> {
      try {
        const res = await FetchService.fetchData(
          `/trials/${trialId}/schedule-demo`,
          "post",
          { demo_date: demoDate },
          true
        );
        if (res.res.ok) {
          const updated = res.data?.data as TrialMembership | undefined;
          if (updated) {
            upsertLead(this.leads, updated);
          }
          return {
            status: true,
            message: res.data?.message || "Demo scheduled successfully",
            data: updated,
            zoom_join_url: res.data?.zoom_join_url,
            google_event_id: res.data?.google_event_id,
            warnings: res.data?.warnings || [],
          };
        }
        return {
          status: false,
          message: res.data?.message || res.data || "Failed to schedule demo",
        };
      } catch (error) {
        console.error("Error scheduling demo:", error);
        return { status: false, message: "Failed to schedule demo" };
      }
    },
  },
});

export default useLeads;
